import React, { useState, useEffect } from "react";
import moment from "moment";

import { SkedIcon } from "ui";
import { POST_STATES } from "constants/PostsView";
import { FIELDS } from "constants/PostFormFields";
import { DATE_FORMAT } from "constants/common";
import DateTimePicker from "components/DateTimePicker/DateTimePicker";
import ngDeps from "ng-react-directives/ngr-injector";

import { dateInThePast } from "utils/dates";
import { QuestionIconWithTooltip } from "ui";
import { ExternalLink } from "ui/Link";
import { Select } from "ui/ecosystems/forms";
import styled from "styled-components";
import { getScheduleDisplay } from "./post-form-actions.utils";
import { Post } from "../../../../features/post";

const POST_NOW = `${POST_STATES.SCHEDULED}-NOW`;

const PostFormActions = ({
  post,
  samePostState,
  timePreference,
  onChange,
  disabled = false,
  canQueuePosts = true,
  isSummary,
  postState,
  inBulkTab,
}: {
  post: Post;
  samePostState: boolean;
  postState: string;
  timePreference: string;
  onChange: (changes: Partial<Post>) => void;
  disabled?: boolean;
  canQueuePosts?: boolean;
  isSummary?: boolean;
  inBulkTab?: boolean;
}) => {
  const { $rootScope } = ngDeps;
  const timezone = $rootScope.user.timezone;
  const selected =
    postState === POST_STATES.SCHEDULED && post.isNow ? POST_NOW : postState;
  const [scheduledFor, setScheduledFor] = useState(getDefaultScheduledFor());
  const [inThePast, setInThePast] = useState(false);

  function onDateChange(newDateTime: string) {
    setScheduledFor(newDateTime);
    setDatePickerVisible(false);
    setInThePast(dateInThePast(newDateTime));
    onChange && onChange({ [FIELDS.WHEN]: newDateTime });
  }

  function getDefaultScheduledFor() {
    if (post.when && !moment(post.when).isValid()) {
      throw new Error(
        `The selected date of ${post.when} is not valid. Please try again or contact support if the issue persists.`
      );
    }

    return (
      post.when ? moment(post.when) : moment().tz(timezone).add(5, "minutes")
    ).format(DATE_FORMAT);
  }

  useEffect(() => {
    if (selected === POST_STATES.SCHEDULED) {
      const time = getDefaultScheduledFor();
      setScheduledFor(time);
      if (!post.when) {
        onChange({ [FIELDS.WHEN]: time });
      }
      setInThePast(dateInThePast(time));
    }
  }, [selected]);

  const queueTooltip = (
    <div>
      Use your recurring posting schedule. Click for more (in a new window).
    </div>
  );
  const disabledQueueTooltip = (
    <div>
      Set up Queue for all your selected accounts to post on a recurring
      schedule. Click for more (in a new window).
    </div>
  );

  let config = [
    {
      value: POST_STATES.DRAFT,
      title: (
        <StateOption className="flex items-center">
          <SkedIcon icon="icon-send-draft" className="mr-2" />
          Draft
        </StateOption>
      ),
      label: (
        <StateLabel className="flex items-center">
          <SkedIcon icon="icon-send-draft" className="mr-2" />
          Draft
        </StateLabel>
      ),
    },
    {
      value: POST_STATES.QUEUED,
      title: (
        <StateOption
          className={`flex items-center ${
            canQueuePosts ? `` : `text-gray-600 cursor-not-allowed`
          }`}
        >
          <SkedIcon icon="icon-queue" className="mr-2" />
          Queue post&nbsp;
          <ExternalLink
            href={`https://help.skedsocial.com/en/articles/3811779-use-the-queue-function`}
          >
            <QuestionIconWithTooltip
              tooltip={canQueuePosts ? queueTooltip : disabledQueueTooltip}
            />
          </ExternalLink>
        </StateOption>
      ),
      // don't show tooltip when displayed in the input
      label: (
        <StateLabel className="flex items-center">
          <SkedIcon icon="icon-queue" className="mr-2" />
          Queue post
        </StateLabel>
      ),
      isDisabled: !canQueuePosts,
    },
    {
      value: POST_STATES.SCHEDULED,
      title: (
        <StateOption className="flex items-center">
          <SkedIcon icon="icon-upcoming" className="mr-2" />
          Schedule for
        </StateOption>
      ),
      label: (
        <StateLabel className="flex items-center">
          <SkedIcon icon="icon-upcoming" className="mr-2" />
          Schedule for
        </StateLabel>
      ),
    },
    {
      value: POST_NOW,
      title: (
        <StateOption className="flex items-center">
          <SkedIcon icon="icon-upcoming" className="mr-2" />
          Post Now
        </StateOption>
      ),
      label: (
        <StateLabel className="flex items-center">
          <SkedIcon icon="icon-upcoming" className="mr-2" />
          Post Now
        </StateLabel>
      ),
    },
  ];
  // add an archived option here for when user uses 'post again'
  if (selected === POST_STATES.ARCHIVED) {
    config.push({
      id: 3,
      value: POST_STATES.ARCHIVED,
      title: (
        <StateOption className="flex items-center">
          Archived - choose one…
        </StateOption>
      ),
      label: (
        <StateLabel className="flex items-center">
          Archived - choose one…
        </StateLabel>
      ),
      selected: selected === POST_STATES.ARCHIVED,
      isDisabled: true,
    });
  }

  const handlePostStateChange = (value: string) => {
    onChange &&
      onChange({
        [FIELDS.POST_STATE]: value === POST_NOW ? POST_STATES.SCHEDULED : value,
        [FIELDS.IS_NOW]: value === POST_NOW,
      });
  };
  const [datePickerVisible, setDatePickerVisible] = useState(false);

  const scheduledClasses = "bg-primary-37 text-black";
  const scheduledErrorClasses = "bg-red-100 text-red-500";

  return (
    <div
      className="flex whitespace-nowrap"
      data-testid="postCreateTypeSelectWrapper"
    >
      {!isSummary && (
        <ActionSelect
          className={"mr-4"}
          options={config}
          defaultValue={config[0]}
          value={selected}
          closeMenuOnSelect
          isMulti={false}
          isSearchable={false}
          onChange={handlePostStateChange}
          renderOption={(option) => option.title}
          renderLabel={(option) => option.label}
          placeholder={"Select one"}
          menuPlacement={"auto"}
          hoverEffectOnOptions
        />
      )}
      {selected === POST_STATES.ARCHIVED && (
        <DisplayBox className="text-black bg-red-50">
          Select action for this post.
        </DisplayBox>
      )}
      {selected === POST_STATES.DRAFT && (
        <DisplayBox className="text-black bg-primary-37">
          {samePostState
            ? "This post is a draft"
            : "This post will be sent to drafts"}
        </DisplayBox>
      )}
      {selected === POST_STATES.QUEUED && (
        <DisplayBox className="text-black bg-primary-37">
          {samePostState
            ? "This post is queued"
            : "This post will be sent to the queue"}
        </DisplayBox>
      )}
      {selected === POST_STATES.SCHEDULED && !isSummary && (
        <DisplayBox
          className={`relative text-black ${
            inThePast ? scheduledErrorClasses : scheduledClasses
          }`}
        >
          {inBulkTab ? "" : "Scheduled for:"}
          <span
            className={`underline cursor-pointer ml-4 ${
              inThePast ? "text-red-400" : "text-primary-500"
            }`}
            onClick={() =>
              !disabled && setDatePickerVisible(!datePickerVisible)
            }
          >
            {getScheduleDisplay(scheduledFor, timePreference)}
          </span>
          {datePickerVisible && (
            <div
              className="absolute"
              style={{ transform: "translate(-35%, -70%)", zIndex: 1000 }}
            >
              <DateTimePicker
                selectedDate={scheduledFor}
                onChange={onDateChange}
                timePreference={timePreference}
                onClose={() => setDatePickerVisible(false)}
              />
            </div>
          )}
        </DisplayBox>
      )}
      {selected === `${POST_STATES.SCHEDULED}-NOW` && (
        <DisplayBox className="bg-primary-200 text-black">
          Scheduled to post ASAP
        </DisplayBox>
      )}
      {selected === POST_STATES.SCHEDULED && isSummary && (
        <DisplayBoxSuccess>
          Posted: {getScheduleDisplay(scheduledFor, timePreference)}
        </DisplayBoxSuccess>
      )}
    </div>
  );
};

const DisplayBox = ({ children, className = "" }) => {
  return (
    <div
      className={`flex items-center rounded-lg font-medium px-4 flex-wrap ${className}`}
    >
      {children}
    </div>
  );
};
const DisplayBoxSuccess = styled.div`
  height: 48px;
  background: #f0f8e7;
  color: #66b913;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  padding: 0 20px;
  border-radius: 5px;
`;
const ActionSelect = styled(Select)`
  font-size: 14px;
  line-height: 38px;
  min-width: 190px;
`;
const StateOption = styled.div`
  font-size: 14px;
`;
const StateLabel = styled.div`
  font-size: 14px;
  line-height: 38px;
`;

export default PostFormActions;
